import { useQuery } from "@tanstack/react-query";
import { OptionSelect } from "Common/Components";
import { useFormik } from "formik";
import { validateSelect } from "helpers/functionsHelpers";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { productTransfer } from "interfaces";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useInvAdjustmentStore } from "../stores/storeInventoryAdjustment";
import { getProductsByWarehouse } from "services";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";

export const useDetailsInvAdjustmentForm = () => {
  const { t } = useTranslation();

  const {
   
    payloadTransfer,
    productsTransfer,
    setPayloadTransfer,
    addProductTransfer,
    removeProductTransfer,
  } = useInvAdjustmentStore();

  const [productOptions, setProductOptions] = useState<OptionSelect[]>([]);
  const [productRows, setProductRows] = useState<any[]>([]);

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "ID",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("transferBetweenWarehouse.table.columns.product"),
        accessorKey: "product",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("transferBetweenWarehouse.table.columns.productCode"),
        accessorKey: "productCode",
        enableColumnFilter: false,
        enableSorting: true,
      },

      {
        header: t("transferBetweenWarehouse.table.columns.quantity"),
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: true,
      },

      {
        header: t("transferBetweenWarehouse.table.columns.actions"),
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: { getValue: () => any }) => getValue(),
      },
    ],
    [t]
  );

  const { data: productsByWarehouse } = useQuery({
    queryKey: ["productsByWarehouse", payloadTransfer.warehouseId],
    queryFn: () =>
      getProductsByWarehouse(payloadTransfer.warehouseId.toString()),
    enabled:
      !!payloadTransfer.warehouseId &&
      payloadTransfer.warehouseId !== 0,
  });

  const initialValuesDetails = {
    product: "",
    quantity: "",
  };

  const validatioSchemaDetails = Yup.object().shape({
    product: validateSelect(t),
    quantity: Yup.number()
      .required(t("validationForms.required"))
      .min(1, t("validationForms.min", { min: 1 })),
  });
  useEffect(() => {
    if (Array.isArray(productsByWarehouse)) {
      const options = productsByWarehouse.map((product) => {
        return {
          label:
            product.productCode.description +
            " - " +
            product.stock +
            " " +
            t("transferBetweenWarehouse.units") +
            " - " +
            t("transferBetweenWarehouse.productCode") +
            " " +
            product.productCode.productCode,
          value: product.productCode.productCode,
        };
      });
      setProductOptions(options);
    } else {
      console.error("productsByWarehouse is not an array:", productsByWarehouse);
    }
  }, [productsByWarehouse, t]);

  const formikDetails = useFormik({
    initialValues: initialValuesDetails,
    validationSchema: validatioSchemaDetails,
    enableReinitialize: true,
    onSubmit: (values) => {
      const findProductAdded = productsTransfer.find(
        (item) => item.product_id === Number(values.product)
      );

      if (findProductAdded) {
        showNotifications(
          NotificationType.ERROR,
          t("InventoryAdjustment.productAlreadyAdded")
        );
        return;
      }

      if (productsByWarehouse === undefined) return;

      const productFind = productsByWarehouse.find(
        (item) => item.productCode.productCode === values.product
      );

      if (productFind && productFind?.stock < Number(values.quantity)) {
        showNotifications(
          NotificationType.ERROR,
          t("InventoryAdjustment.insufficientStock")
        );
        return;
      }

      if (productFind) {
        const localPayload: productTransfer = {
          product_id: Number(productFind.productCode.productCode),
          quantity: Number(values.quantity),
          product: productFind,
        };

        addProductTransfer(localPayload);

        formikDetails.resetForm();

        showNotifications(
          NotificationType.SUCCESS,
          t("transferBetweenWarehouse.productAdded")
        );
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("transferBetweenWarehouse.productNotFound")
        );
      }
    },
  });

  useEffect(() => {
    const rows = createTableRows(productsTransfer);

    setProductRows(rows);
  }, [productsTransfer]);

  const createTableRows = (data: productTransfer[]) => {
    return data.map((item, index) => {
      return {
        ID: index + 1,
        product: item.product?.productCode.description,
        productCode: item.product?.productCode.productCode,
        quantity: item.quantity,
        actions: (
          <Link
            to="#!"
            className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md remove-item-btn bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
            onClick={() => {
              onClickDelete(item.product_id);
            }}
          >
            <Trash2 className="size-4" />
          </Link>
        ),
      };
    });
  };

  const onClickDelete = (id: number) => {
    removeProductTransfer(id);

    showNotifications(
      NotificationType.SUCCESS,
      t("transferBetweenWarehouse.productDeleted")
    );
  };

  return {
   
    t,
    productOptions,
    productRows,
    columns,
    formikDetails,

    
  };
};
